import axios, { type AxiosRequestConfig } from 'axios';

import {
  normaliseShotPlan,
  type ShotPlan,
  type StoryboardBeatInput,
  type StoryboardPipeline,
  type StoryboardPromotion,
  type VideoAspectRatio,
} from '@/store/slices/creativeJobs';

import type { StoryboardDraft, StoryboardTemplateId } from './storyboard';
import { isVideoAspect } from './video-composer';
import { DEFAULT_V1_TARGET_SECONDS, DEFAULT_VIDEO_MODEL, clampClipLength } from './video-models';
import type { VideoSubjectType } from './video-subjects';

/**
 * The Video Ads library: DRAFT-FIRST video ad entities the owner manages. A `video` creatives row is
 * created the moment "Create new" is clicked ({@link upsertVideoDraft} with no id), then every edit upserts
 * it, and the finished mp4 is written back onto it once the run is done. Each carries its name, poster
 * (`image_url` = the key visual), the assembled mp4 (`video_url`, empty for a draft), and the full editable
 * draft (`storyboard`) so it can be edited + REOPENED. Delete/restore reuse the banner-version endpoints.
 */

/** One rendered version of the finished ad, kept as a history so regenerating an ad does NOT lose the
 *  previous renders. Newest first. `created_at` is the render's completion time as an RFC 3339 string (the
 *  wire's date-time format, same as `SavedVideo.created_at`). */
export interface FinishedAdVersion {
  /** The finished mp4. Absent for a STORYBOARD-ONLY version (the owner composed a storyboard but has not
   *  rendered it yet). */
  url?: string;
  /** The storyboard this version was composed/rendered from, so selecting a version restores its beats into
   *  the editor. Absent for a legacy render-only entry (predating storyboard+video pairing). */
  storyboard?: SavedStoryboard;
  created_at: string;
  /** True once this version has been RENDERED: generating a video LOCKS it. Editing a locked version forks a
   *  new unlocked "Latest" first, so a generated cut is never mutated in place. Set by the backend at render. */
  locked?: boolean;
}

/** How many finished-ad versions to retain per entity (newest kept). Mirrors the backend cap
 *  `svd.MaxVideoVersions`; a version is just a URL + timestamp, so the cap bounds row growth, not history. */
export const MAX_FINISHED_AD_VERSIONS = 20;

/** The persisted editable draft of a video ad entity (matches the backend storyboard_params). */
export interface SavedStoryboard {
  aspect_ratio: VideoAspectRatio;
  /** What this ad advertises (promotion / game / gamification). Absent = a legacy promotion ad. Drives the
   *  composer's subject-aware labels; round-tripped so a reopened ad keeps its category. */
  subject_type?: VideoSubjectType;
  template_id?: string;
  video_model?: string;
  /** The owner's free-text steering prompt, persisted so it survives edit/reopen. */
  prompt?: string;
  brand: { name?: string; domain?: string; logo_url?: string; cta_text?: string };
  /** The promotions this storyboard advertises (multi-promo). Empty/absent = a legacy single-promo ad,
   *  synthesized on read from the entity's image_url + brand.cta_text. */
  promotions?: StoryboardPromotion[];
  beats: StoryboardBeatInput[];
  /** Owner-supplied mood-board reference images (CDN urls). snake_case to round-trip through the backend
   *  storyboard_params (matches the Go json tag), so they survive edit/reopen. */
  mood_images?: string[];
  /** The owner's chosen total ad length (10/15/20/30; a single clip's exact length L); persisted so it
   *  survives edit/reopen/version-switch. */
  target_duration_seconds?: number;
  /** `single_clip` (v2) or `beats` (v1). Absent = a legacy ad = `beats`. Always written by the composer. */
  pipeline?: StoryboardPipeline;
  /** Single clip only: the composed (and owner-edited) timestamped shot plan. */
  shot_plan?: ShotPlan;
  /** The English vibe brief the ad was composed with (informational). */
  vibe?: string;
  /** v1 only: the compose's ad-level instrumental music brief (hidden, no UI). Part of the storyboard snapshot
   *  (so a version keeps the soundtrack it was composed with); absent on legacy ads and single clips. */
  music?: string;
  /** The ad's language (a brand website language id: `mx`, `br`, `en`...). Absent / '' = an older ad, rendered as
   *  before. Part of every version's snapshot, so a version keeps the language it was composed in. */
  language?: string;
  /** The language the saved storyboard was composed in, when it differs from `language` (the owner picked another one
   *  without composing again). Absent = as `language`. */
  composed_language?: string;
}

/** Narrow a persisted pipeline value (absent / unknown = the legacy v1 `beats`). */
export const pipelineOf = (value: unknown): StoryboardPipeline => (value === 'single_clip' ? 'single_clip' : 'beats');

/** The pipeline an ad opens with in the editor. A NEW ad (created from a video tab this session) takes that tab's
 *  pipeline unless its first write already carries one; a REOPENED ad always keeps its own saved pipeline (absent =
 *  a pre-v2 ad = `beats`), even when it is opened from the other video tab. */
export const adPipeline = (
  saved: unknown,
  tabPipeline: StoryboardPipeline,
  isNew: boolean,
): StoryboardPipeline => (isNew && !saved ? tabPipeline : pipelineOf(saved));

/** The ads a video tab lists: Video Ads v2.0 (`single_clip`) shows only single-clip ads, Video Ads (`beats`) every
 *  other one (explicit `beats` and every pre-v2 ad with no pipeline). Optional-chained: a wire row may lack its
 *  storyboard. */
export const videosForPipeline = (videos: SavedVideo[], pipeline: StoryboardPipeline): SavedVideo[] =>
  videos.filter((v) => pipelineOf(v.storyboard?.pipeline) === pipeline);

/** A library card's glanceable state: a rendered video, a composed-but-unrendered storyboard (v1 beats, or a
 *  single clip's shot plan), or a bare draft. */
export const videoCardState = (v: Pick<SavedVideo, 'video_url' | 'storyboard'>): 'draft' | 'storyboard' | 'video' => {
  if (v.video_url) return 'video';
  const composed = (v.storyboard?.beats?.length ?? 0) > 0 || (v.storyboard?.shot_plan?.shots?.length ?? 0) > 0;
  return composed ? 'storyboard' : 'draft';
};

/** Pure merge used by the editor's live display: the current session's just-finished render prepended over
 *  the persisted history (newest first, de-duplicated by url, capped). No persistence here (the worker owns the
 *  history); the url dedup lets the server entry, with its authoritative timestamp, win on the next list refresh. */
export const appendFinishedAdVersion = (
  versions: FinishedAdVersion[] | undefined,
  url: string,
  createdAt: string,
  storyboard?: SavedStoryboard,
): FinishedAdVersion[] => {
  const list = Array.isArray(versions) ? versions : [];
  if (!url || list.some((v) => v.url === url)) return list; // already recorded → no change (idempotent)
  return [{ url, storyboard, created_at: createdAt }, ...list].slice(0, MAX_FINISHED_AD_VERSIONS);
};

export interface SavedVideo {
  id: string;
  tenant_id: string;
  /** The owner's label for the entry, shown on the library card and editable in the editor. */
  name: string;
  media_type: string;
  video_url: string;
  /** The finished renders, newest first (`video_url` is `[0].url` once rendered; `[]` for a draft). Written
   *  ONLY by the backend worker at job done; never sent on an upsert (deliberately absent from VideoDraftUpsert). */
  video_versions: FinishedAdVersion[];
  /** The key visual (F0): the poster shown on the tile AND the reopen join key back to the promotion. */
  image_url: string;
  prompt?: string;
  /** 'draft' (created, not generated) | 'ready' (has a video) | 'archived'. */
  status: string;
  archived: boolean;
  /** The generating job; the hook reconciles the mp4 onto the entity when this job lands done. */
  job_id?: string;
  created_at?: string;
  storyboard: SavedStoryboard;
}

/** Body for creating/updating a video ad entity. Omit `id` to create a new draft; include it to update. */
export interface VideoDraftUpsert {
  id?: string;
  name?: string;
  image_url?: string;
  video_url?: string;
  status?: string;
  job_id?: string;
  storyboard?: SavedStoryboard;
  /** When set, this storyboard is appended as a new video-less VERSION to the ad's history (the owner
   *  composed/regenerated the storyboard). Independent of the draft `storyboard` update above. */
  storyboard_version?: SavedStoryboard;
  /** The created_at key to mint for {@link storyboard_version} (RFC 3339). The FRONTEND supplies it so the
   *  optimistic entry, the selected version and the persisted key are ONE value, and a later Generate can
   *  target this exact version to fill it in place. Only meaningful alongside `storyboard_version`. */
  storyboard_version_at?: string;
}

/** A version's stable identity key, normalized to epoch-ms text so the DB->Go->JSON round-trip of a
 *  created_at string (which can reformat fractional digits / timezone offset) still compares equal. Used for
 *  every version key: the picker value, the selected key, and the render target. An unparseable value falls
 *  back to itself so a legacy/odd timestamp still keys stably (just not normalized). */
export const versionKey = (createdAt: string): string => {
  const ms = Date.parse(createdAt);
  return Number.isNaN(ms) ? createdAt : String(ms);
};

const creativesBase = (): string => {
  const cfg = window.config as unknown as Record<string, unknown>;
  return typeof cfg?.creativesApiUrl === 'string' ? (cfg.creativesApiUrl as string).replace(/\/+$/, '') : '';
};

const authConfig = (signal?: AbortSignal): AxiosRequestConfig => {
  const token = localStorage.getItem('access_token') || '';
  return { signal, _owsNoAuthRecovery: true, ...(token ? { headers: { Authorization: token } } : {}) };
};

/** Whether the video library backend is reachable (the service URL is configured). */
export const videoLibraryConfigured = (): boolean => creativesBase() !== '';

/**
 * True when a JSON endpoint answered with a non-JSON body. In practice this is the SPA's index.html
 * (a STRING, served at HTTP 200) returned because the `/__spark` dev proxy is not forwarding or
 * spark-creatives is down. axios only leaves `res.data` as a string when the response was not
 * `application/json`, so a string body from these endpoints ALWAYS signals a misrouted/unreachable
 * backend, never real data. Guarding on it stops a silent `[]` from masking a downed backend as
 * "no videos" (which hid a missing dev proxy as an empty library for hours).
 */
export const isNonJsonBody = (body: unknown): boolean => typeof body === 'string';

/** List a brand's saved video ads, newest first. Returns [] when unconfigured; THROWS (loudly) when the
 *  backend is unreachable / the dev proxy is not forwarding, so callers surface that instead of an empty list. */
export const listVideos = async (tenantId: number, signal?: AbortSignal): Promise<SavedVideo[]> => {
  const base = creativesBase();
  if (!base) return [];
  const res = await axios.get<{ data?: Array<Omit<SavedVideo, 'video_versions'> & { video_versions?: FinishedAdVersion[] }> }>(
    `${base}/creatives/tenant/${tenantId}/videos`,
    authConfig(signal),
  );
  if (isNonJsonBody(res.data)) {
    // The request resolved 2xx but with HTML, not JSON: the `/__spark` dev proxy isn't forwarding, or the
    // backend served its own SPA/error page. Fail loudly rather than parsing it into an empty list.
    const message = `listVideos: ${base} returned a non-JSON (HTML) body. The /__spark dev proxy is not forwarding or spark-creatives is unreachable; treating as an error instead of "no videos".`;
    // eslint-disable-next-line no-console -- surface a masked backend/proxy outage the empty-list path used to hide.
    console.error(`[video-library] ${message}`);
    throw new Error(message);
  }
  const rows = Array.isArray(res.data?.data) ? res.data.data : [];
  // Deploy skew: a backend older than the versions column omits the key; keep the required type honest.
  return rows.map((r) => ({ ...r, video_versions: Array.isArray(r.video_versions) ? r.video_versions : [] }));
};

/** Create (no id) or update (by id) a draft-first video ad entity. Returns the entity id ('' on failure). */
export const upsertVideoDraft = async (
  tenantId: number,
  body: VideoDraftUpsert,
  signal?: AbortSignal,
): Promise<string> => {
  const base = creativesBase();
  if (!base) return '';
  const res = await axios.post<{ id?: string; ok?: boolean }>(
    `${base}/creatives/tenant/${tenantId}/videos`,
    body,
    authConfig(signal),
  );
  return res.data?.id ?? '';
};

/** `composed_language` for a storyboard composed in no language: the service drops an empty value, so "composed in
 *  none" is saved as the undetermined-language tag to tell it from "not recorded" (absent). */
const NO_LANGUAGE = 'und';

/** A saved `composed_language` as the draft holds it: '' for one composed in no language, undefined when absent. */
export const composedLanguageOf = (value: unknown): string | undefined => {
  const v = typeof value === 'string' ? value.trim() : '';
  if (!v) return undefined;
  return v === NO_LANGUAGE ? '' : v;
};

/**
 * Rebuild an editable draft from an entity's persisted params (the inverse of toStoryboardInput), so
 * reopening restores the full editor: aspect from `aspect_ratio`, the end-card CTA from `brand.cta_text`,
 * the beats verbatim, and the steering prompt. Returns the template + model to restore alongside the draft.
 */
export const draftFromStoryboard = (
  sb: SavedStoryboard,
  fallbackImageUrl?: string,
): { draft: StoryboardDraft; templateId?: StoryboardTemplateId; videoModel?: string; prompt: string } => {
  // Promotions: the saved set, else a single synthesized one from the entity's key visual (legacy single-promo).
  const promotions: StoryboardPromotion[] =
    Array.isArray(sb.promotions) && sb.promotions.length > 0
      ? sb.promotions
      : fallbackImageUrl
        ? [{ banner_id: '', image_url: fallbackImageUrl, cta_text: sb.brand?.cta_text ?? '' }]
        : [];
  const beats = (Array.isArray(sb.beats) ? sb.beats : []).map((b) => ({ ...b, promotion_index: b.promotion_index ?? 0 }));
  return {
    draft: {
      // Snap a persisted aspect the picker no longer offers (a legacy 4:3/3:2/3:4 draft) to 16:9, so
      // aspectRatioValue / the picker never hit a dropped ASPECT_META key.
      aspect: isVideoAspect(sb.aspect_ratio) ? sb.aspect_ratio : '16:9',
      cta: sb.brand?.cta_text ?? '',
      promotions,
      beats,
      moodImages: Array.isArray(sb.mood_images) ? sb.mood_images : [],
      // A v1 ad saved without a length reads the default (10s); a single clip's length is resolved by its model.
      targetDurationSeconds:
        sb.target_duration_seconds || (pipelineOf(sb.pipeline) === 'single_clip' ? undefined : DEFAULT_V1_TARGET_SECONDS),
      pipeline: pipelineOf(sb.pipeline),
      // Normalised: the persisted plan comes back through Go, which may omit empty optional fields.
      shotPlan:
        sb.shot_plan && Array.isArray(sb.shot_plan.shots) && sb.shot_plan.shots.length > 0
          ? normaliseShotPlan(sb.shot_plan)
          : undefined,
      // Go `omitempty` drops an empty brief; normalise empty/missing to undefined so both read the same.
      music: typeof sb.music === 'string' && sb.music.trim() !== '' ? sb.music : undefined,
      // Go `omitempty` drops an empty language too: missing and '' both read '' (an older ad).
      language: typeof sb.language === 'string' ? sb.language.trim() : '',
      composedLanguage: composedLanguageOf(sb.composed_language),
    },
    templateId: (sb.template_id || undefined) as StoryboardTemplateId | undefined,
    videoModel: sb.video_model || undefined,
    prompt: sb.prompt ?? '',
  };
};

/** The model + clip length a reopened SINGLE-CLIP ad starts on: its saved model (else the default) and its saved
 *  length clamped to that model's clip lengths. Covers an ad saved before its first compose (no shot plan), which the
 *  plan-based reopen hydration skips, so the owner's pick is not reset to the default and then persisted over. */
export const singleClipSetupOf = (
  sb: Pick<SavedStoryboard, 'video_model' | 'target_duration_seconds'> | undefined,
): { videoModel: string; targetDurationSeconds: number } => {
  const videoModel = sb?.video_model || DEFAULT_VIDEO_MODEL;
  return { videoModel, targetDurationSeconds: clampClipLength(sb?.target_duration_seconds, videoModel) };
};

/** Forward mapper: build the persisted draft (SavedStoryboard) from the editor's live state, for upsert. */
export const storyboardToSaved = (
  draft: StoryboardDraft,
  templateId: string,
  videoModel: string,
  prompt: string,
  brand: { name?: string; domain?: string; logo_url?: string },
  subjectType?: VideoSubjectType,
  vibe?: string,
): SavedStoryboard => ({
  aspect_ratio: draft.aspect,
  subject_type: subjectType,
  template_id: templateId,
  video_model: videoModel,
  prompt,
  brand: { name: brand.name, domain: brand.domain, logo_url: brand.logo_url, cta_text: draft.cta },
  promotions: draft.promotions,
  beats: draft.beats,
  mood_images: draft.moodImages,
  target_duration_seconds: draft.targetDurationSeconds,
  // Written ALWAYS (beats included), so every saved ad records which video tab it belongs to.
  pipeline: draft.pipeline,
  shot_plan: draft.shotPlan,
  vibe,
  music: draft.music,
  language: draft.language || undefined,
  composed_language: draft.composedLanguage === undefined ? undefined : draft.composedLanguage || NO_LANGUAGE,
});
